import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Film, 
  Camera, 
  Mic, 
  Palette, 
  Users, 
  Sparkles, 
  Clapperboard,
  Wand2,
  UtensilsCrossed,
  Box,
  Video,
  Lightbulb,
  FileText,
  Search,
  SlidersHorizontal
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';

const DepartmentPage = () => {
  const { state, city } = useParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filteredDepartments, setFilteredDepartments] = useState([]);

  const departments = [
    {
      id: 1,
      name: 'Art Department',
      icon: Palette,
      description: 'Set designers, art directors, prop masters, and scenic artists',
      count: 1250,
      color: 'from-purple-500 to-pink-500',
      category: 'creative'
    },
    {
      id: 2,
      name: 'Assistant Directors',
      icon: Clapperboard,
      description: 'First AD, second AD, third AD, and production coordinators',
      count: 890,
      color: 'from-blue-500 to-cyan-500',
      category: 'production'
    },
    {
      id: 3,
      name: 'Camera Department',
      icon: Camera,
      description: 'Directors of photography, camera operators, and focus pullers',
      count: 1580,
      color: 'from-orange-500 to-red-500',
      category: 'technical'
    },
    {
      id: 4,
      name: 'Sound Department',
      icon: Mic,
      description: 'Sound designers, boom operators, and audio engineers',
      count: 720,
      color: 'from-green-500 to-emerald-500',
      category: 'technical'
    },
    {
      id: 5,
      name: 'Makeup & Hair',
      icon: Sparkles,
      description: 'Makeup artists, hair stylists, and special effects makeup',
      count: 950,
      color: 'from-pink-500 to-rose-500',
      category: 'creative'
    },
    {
      id: 6,
      name: 'SFX / VFX',
      icon: Wand2,
      description: 'Visual effects artists, compositors, and 3D animators',
      count: 2100,
      color: 'from-violet-500 to-purple-500',
      category: 'technical'
    },
    {
      id: 7,
      name: 'Food Stylist',
      icon: UtensilsCrossed,
      description: 'Professional food stylists and culinary artists',
      count: 340,
      color: 'from-yellow-500 to-orange-500',
      category: 'creative'
    },
    {
      id: 8,
      name: 'Model Making',
      icon: Box,
      description: 'Miniature artists, prop fabricators, and model builders',
      count: 480,
      color: 'from-teal-500 to-cyan-500',
      category: 'creative'
    },
    {
      id: 9,
      name: 'Production',
      icon: Users,
      description: 'Line producers, production managers, and coordinators',
      count: 1650,
      color: 'from-indigo-500 to-blue-500',
      category: 'production'
    },
    {
      id: 10,
      name: 'Lighting Department',
      icon: Lightbulb,
      description: 'Gaffers, best boys, and lighting technicians',
      count: 680,
      color: 'from-amber-500 to-yellow-500',
      category: 'technical'
    },
    {
      id: 11,
      name: 'Post Production',
      icon: Video,
      description: 'Editors, colorists, and post supervisors',
      count: 1420,
      color: 'from-red-500 to-pink-500',
      category: 'technical'
    },
    {
      id: 12,
      name: 'Script Department',
      icon: FileText,
      description: 'Script supervisors, writers, and script coordinators',
      count: 560,
      color: 'from-cyan-500 to-blue-500',
      category: 'production'
    }
  ];

  const filterDepartments = () => {
    let filtered = departments;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(dept => dept.category === selectedCategory);
    }

    if (searchQuery) {
      filtered = filtered.filter(dept => 
        dept.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        dept.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredDepartments(filtered);
  };

  useEffect(() => {
    filterDepartments();
  }, [searchQuery, selectedCategory]);

  const handleDepartmentClick = (department) => {
    const departmentSlug = department.name.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-');
    navigate(`/departments/${state}/${city}/${departmentSlug}`);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <Navigation showBackButton={true} backTo="/" />

      {/* Header Section */}
      <section className="py-12 bg-gradient-to-b from-orange-ultralight to-background border-b border-border">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge className="mb-4 bg-accent/10 text-accent border-accent/30">
              {city}, {state}
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold mb-4 text-foreground">
              Explore Film Departments
            </h2>
            <p className="text-lg text-muted-foreground">
              Connect with {filteredDepartments.reduce((acc, dept) => acc + dept.count, 0).toLocaleString()}+ verified professionals in {city}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Global Search Section */}
      <section className="py-8 bg-background sticky top-20 z-40 border-b border-border">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="max-w-4xl mx-auto"
          >
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search professionals, departments, or vendors across all categories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-14 bg-card border-border text-base shadow-lg"
              />
            </div>
            {searchQuery && (
              <p className="text-sm text-muted-foreground mt-2 text-center">
                Showing results for &quot;{searchQuery}&quot; - {filteredDepartments.length} departments found
              </p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-8 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="max-w-4xl mx-auto"
          >
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search departments..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-card border-border"
                />
              </div>
              <div className="flex gap-4">
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-[180px] bg-card border-border">
                    <SlidersHorizontal className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="creative">Creative</SelectItem>
                    <SelectItem value="technical">Technical</SelectItem>
                    <SelectItem value="production">Production</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Departments Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          {filteredDepartments.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <div className="bg-muted/20 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <Search className="w-10 h-10 text-muted-foreground" />
              </div>
              <h3 className="text-2xl font-semibold mb-2 text-foreground">No departments found</h3>
              <p className="text-muted-foreground">Try adjusting your search or filters</p>
            </motion.div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredDepartments.map((department, index) => (
                <motion.div
                  key={department.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card 
                    className="h-full bg-card backdrop-blur border-border hover:border-accent/50 transition-all duration-300 cursor-pointer group overflow-hidden hover:shadow-lg"
                    onClick={() => handleDepartmentClick(department)}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between mb-4">
                        <div className={`bg-gradient-to-br ${department.color} p-3 rounded-xl group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                          <department.icon className="w-6 h-6 text-white" />
                        </div>
                        <Badge variant="secondary" className="bg-accent/10 text-accent border-accent/20">
                          {department.count}+
                        </Badge>
                      </div>
                      <CardTitle className="text-xl group-hover:text-accent transition-colors text-foreground">
                        {department.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {department.description}
                      </p>
                      <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                        <span className="text-xs text-muted-foreground uppercase tracking-wide">
                          {department.category}
                        </span>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-accent hover:text-accent hover:bg-accent/10"
                        >
                          View Profiles
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-background to-orange-ultralight">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
              Can't Find What You're Looking For?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Post your requirements and let professionals come to you
            </p>
            <Button size="lg" className="bg-accent hover:bg-accent/90 text-primary-foreground font-semibold shadow-lg">
              Post Your Requirements
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12 bg-card">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img src="/logo.png" alt="Shoot Base" className="h-12 w-auto object-contain" />
                <div>
                  <h3 className="text-lg font-bold text-foreground">Shoot Base</h3>
                  <p className="text-xs text-muted-foreground">Film Industry Platform</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-4 max-w-md">
                Built by film professionals, for film professionals. Your one-stop platform to source, connect, and create.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-foreground">Platform</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#" className="hover:text-accent transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-accent transition-colors">How it Works</a></li>
                <li><a href="#" className="hover:text-accent transition-colors">Success Stories</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-foreground">Support</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#" className="hover:text-accent transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-accent transition-colors">Contact Us</a></li>
                <li><a href="#" className="hover:text-accent transition-colors">Privacy Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-border mt-8 pt-8 text-center text-sm text-muted-foreground">
            <p>© 2024 Shoot Base. Built with passion for the film industry.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DepartmentPage;